import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface AdPlacement {
  id: string;
  label: string;
  placement: string;
  pageTypes: string[];
  enabled: boolean;
  code: string;
  sticky: boolean;
  device: 'all' | 'desktop' | 'mobile';
}

export interface AffiliateOffer {
  id: string;
  name: string;
  url: string;
  buttonText: string;
  trackingLabel: string;
  description: string;
  pageTypes: string[];
  placement: string;
  enabled: boolean;
  ctaText: string;
  assignedStates?: string[];
  assignedCities?: string[];
}

export interface AdminBlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  author: string;
  excerpt: string;
  image: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  body: string;
  relatedCity: string;
  relatedState: string;
  publishDate: string;
  featured: boolean;
  status: 'published' | 'draft';
  createdAt: string;
  updatedAt: string;
}

export interface LocationOverride {
  slug: string;
  countrySlug: string;
  stateSlug: string;
  citySlug: string;
  introText: string;
  seoBlurb: string;
  faqContent: Array<{ q: string; a: string }>;
  nearbyCities: string[];
  taxRate?: number;
  localTaxRate?: number;
  metaTitle: string;
  metaDescription: string;
  updatedAt: string;
}

export interface LinkSettings {
  popularCalculators: string[];
  cityRelatedPosts: Record<string, string[]>;
  blogRelatedLocations: Record<string, string[]>;
}

export interface SEOSettings {
  siteTitle: string;
  metaTitleTemplate: string;
  metaDescTemplate: string;
  defaultOgImage: string;
  robotsTxt: string;
  schemaSettings: {
    faqEnabled: boolean;
    breadcrumbsEnabled: boolean;
    blogEnabled: boolean;
    webpageEnabled: boolean;
  };
}

export interface AnalyticsSettings {
  gaId: string;
  gaEnabled: boolean;
  gscCode: string;
  headerScripts: string;
  footerScripts: string;
}

export interface NewsletterSettings {
  formUrl: string;
  enabled: { homepage: boolean; blog: boolean; calculator: boolean };
  headline: string;
  subheadline: string;
  buttonText: string;
}

export interface ActivityItem {
  id: string;
  type: 'blog' | 'location' | 'monetization' | 'seo' | 'analytics' | 'newsletter';
  action: string;
  target: string;
  timestamp: string;
}

interface AdminContextType {
  isAuthenticated: boolean;
  login: (password: string) => boolean;
  logout: () => void;
  changePassword: (current: string, newPass: string) => boolean;

  seoSettings: SEOSettings;
  updateSEOSettings: (s: Partial<SEOSettings>) => void;

  analyticsSettings: AnalyticsSettings;
  updateAnalyticsSettings: (s: Partial<AnalyticsSettings>) => void;

  newsletterSettings: NewsletterSettings;
  updateNewsletterSettings: (s: Partial<NewsletterSettings>) => void;

  adPlacements: AdPlacement[];
  saveAdPlacement: (p: AdPlacement) => void;
  deleteAdPlacement: (id: string) => void;
  toggleAdPlacement: (id: string) => void;

  affiliateOffers: AffiliateOffer[];
  saveAffiliateOffer: (o: AffiliateOffer) => void;
  deleteAffiliateOffer: (id: string) => void;
  toggleAffiliateOffer: (id: string) => void;

  adminBlogPosts: AdminBlogPost[];
  saveBlogPost: (p: AdminBlogPost) => void;
  deleteBlogPost: (id: string) => void;
  getBlogPost: (id: string) => AdminBlogPost | undefined;

  locationOverrides: Record<string, LocationOverride>;
  saveLocationOverride: (slug: string, data: LocationOverride) => void;
  getLocationOverride: (slug: string) => LocationOverride | undefined;

  linkSettings: LinkSettings;
  updateLinkSettings: (s: Partial<LinkSettings>) => void;

  recentActivity: ActivityItem[];
  addActivity: (item: Omit<ActivityItem, 'id' | 'timestamp'>) => void;
}

const DEFAULT_SEO: SEOSettings = {
  siteTitle: 'MySalaryCalculator.net',
  metaTitleTemplate: '{page_title} | MySalaryCalculator.net',
  metaDescTemplate: 'Calculate your take-home salary after taxes in {location}. Free 2026 salary calculator with state and local tax breakdown.',
  defaultOgImage: 'https://mysalarycalculator.net/og-default.png',
  robotsTxt: 'User-agent: *\nAllow: /\nDisallow: /admin/\n\nSitemap: https://mysalarycalculator.net/sitemap.xml',
  schemaSettings: {
    faqEnabled: true,
    breadcrumbsEnabled: true,
    blogEnabled: true,
    webpageEnabled: true,
  },
};

const DEFAULT_ANALYTICS: AnalyticsSettings = {
  gaId: 'G-NGXJTGPPG5',
  gaEnabled: true,
  gscCode: '',
  headerScripts: '',
  footerScripts: '',
};

const DEFAULT_NEWSLETTER: NewsletterSettings = {
  formUrl: '',
  enabled: { homepage: true, blog: true, calculator: false },
  headline: 'Get Weekly Tax Saving Tips',
  subheadline: 'Join 12,000+ professionals who get our free salary and tax insights every Tuesday.',
  buttonText: 'Subscribe Free',
};

const DEFAULT_ADS: AdPlacement[] = [
  { id: '1', label: 'Header Ad', placement: 'header', pageTypes: ['all'], enabled: true, code: '', sticky: false, device: 'all' },
  { id: '2', label: 'Sidebar Ad', placement: 'sidebar', pageTypes: ['calculator', 'state'], enabled: true, code: '', sticky: true, device: 'desktop' },
  { id: '3', label: 'Below Results Ad', placement: 'below-results', pageTypes: ['calculator'], enabled: true, code: '', sticky: false, device: 'all' },
  { id: '4', label: 'In-Content Ad', placement: 'in-content', pageTypes: ['blog'], enabled: true, code: '', sticky: false, device: 'all' },
  { id: '5', label: 'Sticky Footer Ad', placement: 'sticky-footer', pageTypes: ['all'], enabled: false, code: '', sticky: true, device: 'mobile' },
];

const DEFAULT_AFFILIATES: AffiliateOffer[] = [
  { id: '1', name: 'TurboTax', url: '#', buttonText: 'File with TurboTax', trackingLabel: 'turbotax-main', description: 'Top-rated tax software', pageTypes: ['calculator', 'blog'], placement: 'below-results', enabled: true, ctaText: 'Compare Tax Software' },
  { id: '2', name: 'H&R Block', url: '#', buttonText: 'Try H&R Block', trackingLabel: 'hrblock-main', description: 'Professional tax help online', pageTypes: ['calculator'], placement: 'sidebar', enabled: true, ctaText: 'Find Tax Help Near You' },
  { id: '3', name: 'Gusto Payroll', url: '#', buttonText: 'Try Gusto Free', trackingLabel: 'gusto-main', description: 'Payroll & HR platform', pageTypes: ['blog'], placement: 'in-content', enabled: true, ctaText: 'Find Payroll Tools' },
];

const DEFAULT_LINKS: LinkSettings = {
  popularCalculators: ['usa/ny/manhattan', 'usa/ca/san-francisco', 'usa/tx/austin', 'usa/fl/miami'],
  cityRelatedPosts: {},
  blogRelatedLocations: {},
};

const AdminContext = createContext<AdminContextType | null>(null);

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return sessionStorage.getItem('adminAuth') === 'true';
  });

  const [seoSettings, setSeoSettings] = useState<SEOSettings>(() => load('admin_seo', DEFAULT_SEO));
  const [analyticsSettings, setAnalyticsSettings] = useState<AnalyticsSettings>(() => load('admin_analytics', DEFAULT_ANALYTICS));
  const [newsletterSettings, setNewsletterSettings] = useState<NewsletterSettings>(() => load('admin_newsletter', DEFAULT_NEWSLETTER));
  const [adPlacements, setAdPlacements] = useState<AdPlacement[]>(() => load('admin_ads', DEFAULT_ADS));
  const [affiliateOffers, setAffiliateOffers] = useState<AffiliateOffer[]>(() => load('admin_affiliates', DEFAULT_AFFILIATES));
  const [adminBlogPosts, setAdminBlogPosts] = useState<AdminBlogPost[]>(() => load('admin_blog_posts', []));
  const [locationOverrides, setLocationOverrides] = useState<Record<string, LocationOverride>>(() => load('admin_location_overrides', {}));
  const [linkSettings, setLinkSettings] = useState<LinkSettings>(() => load('admin_links', DEFAULT_LINKS));
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>(() => load('admin_activity', []));

  const login = useCallback((password: string): boolean => {
    const storedPw = localStorage.getItem('adminPassword') || 'admin2026!';
    if (password === storedPw) {
      sessionStorage.setItem('adminAuth', 'true');
      setIsAuthenticated(true);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
  }, []);

  const changePassword = useCallback((current: string, newPass: string): boolean => {
    const storedPw = localStorage.getItem('adminPassword') || 'admin2026!';
    if (current !== storedPw) return false;
    localStorage.setItem('adminPassword', newPass);
    return true;
  }, []);

  const addActivity = useCallback((item: Omit<ActivityItem, 'id' | 'timestamp'>) => {
    setRecentActivity(prev => {
      const next = [{ ...item, id: Date.now().toString(), timestamp: new Date().toISOString() }, ...prev].slice(0, 50);
      save('admin_activity', next);
      return next;
    });
  }, []);

  const updateSEOSettings = useCallback((s: Partial<SEOSettings>) => {
    setSeoSettings(prev => { const next = { ...prev, ...s }; save('admin_seo', next); return next; });
    addActivity({ type: 'seo', action: 'Updated', target: 'SEO Settings' });
  }, [addActivity]);

  const updateAnalyticsSettings = useCallback((s: Partial<AnalyticsSettings>) => {
    setAnalyticsSettings(prev => { const next = { ...prev, ...s }; save('admin_analytics', next); return next; });
    addActivity({ type: 'analytics', action: 'Updated', target: 'Analytics Settings' });
  }, [addActivity]);

  const updateNewsletterSettings = useCallback((s: Partial<NewsletterSettings>) => {
    setNewsletterSettings(prev => { const next = { ...prev, ...s }; save('admin_newsletter', next); return next; });
    addActivity({ type: 'newsletter', action: 'Updated', target: 'Newsletter Settings' });
  }, [addActivity]);

  const saveAdPlacement = useCallback((p: AdPlacement) => {
    setAdPlacements(prev => {
      const exists = prev.find(x => x.id === p.id);
      const next = exists ? prev.map(x => x.id === p.id ? p : x) : [...prev, p];
      save('admin_ads', next);
      return next;
    });
    addActivity({ type: 'monetization', action: 'Saved', target: `Ad: ${p.label}` });
  }, [addActivity]);

  const deleteAdPlacement = useCallback((id: string) => {
    setAdPlacements(prev => { const next = prev.filter(x => x.id !== id); save('admin_ads', next); return next; });
  }, []);

  const toggleAdPlacement = useCallback((id: string) => {
    setAdPlacements(prev => { const next = prev.map(x => x.id === id ? { ...x, enabled: !x.enabled } : x); save('admin_ads', next); return next; });
  }, []);

  const saveAffiliateOffer = useCallback((o: AffiliateOffer) => {
    setAffiliateOffers(prev => {
      const exists = prev.find(x => x.id === o.id);
      const next = exists ? prev.map(x => x.id === o.id ? o : x) : [...prev, o];
      save('admin_affiliates', next);
      return next;
    });
    addActivity({ type: 'monetization', action: 'Saved', target: `Affiliate: ${o.name}` });
  }, [addActivity]);

  const deleteAffiliateOffer = useCallback((id: string) => {
    setAffiliateOffers(prev => { const next = prev.filter(x => x.id !== id); save('admin_affiliates', next); return next; });
  }, []);

  const toggleAffiliateOffer = useCallback((id: string) => {
    setAffiliateOffers(prev => { const next = prev.map(x => x.id === id ? { ...x, enabled: !x.enabled } : x); save('admin_affiliates', next); return next; });
  }, []);

  const saveBlogPost = useCallback((p: AdminBlogPost) => {
    setAdminBlogPosts(prev => {
      const exists = prev.find(x => x.id === p.id);
      const next = exists ? prev.map(x => x.id === p.id ? p : x) : [...prev, p];
      save('admin_blog_posts', next);
      return next;
    });
    addActivity({ type: 'blog', action: p.status === 'published' ? 'Published' : 'Saved draft', target: p.title });
  }, [addActivity]);

  const deleteBlogPost = useCallback((id: string) => {
    setAdminBlogPosts(prev => { const next = prev.filter(x => x.id !== id); save('admin_blog_posts', next); return next; });
  }, []);

  const getBlogPost = useCallback((id: string) => adminBlogPosts.find(x => x.id === id), [adminBlogPosts]);

  const saveLocationOverride = useCallback((slug: string, data: LocationOverride) => {
    setLocationOverrides(prev => { const next = { ...prev, [slug]: data }; save('admin_location_overrides', next); return next; });
    addActivity({ type: 'location', action: 'Updated', target: slug });
  }, [addActivity]);

  const getLocationOverride = useCallback((slug: string) => locationOverrides[slug], [locationOverrides]);

  const updateLinkSettings = useCallback((s: Partial<LinkSettings>) => {
    setLinkSettings(prev => { const next = { ...prev, ...s }; save('admin_links', next); return next; });
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('adminPassword')) {
      localStorage.setItem('adminPassword', 'admin2026!');
    }
  }, []);

  return (
    <AdminContext.Provider value={{
      isAuthenticated, login, logout, changePassword,
      seoSettings, updateSEOSettings,
      analyticsSettings, updateAnalyticsSettings,
      newsletterSettings, updateNewsletterSettings,
      adPlacements, saveAdPlacement, deleteAdPlacement, toggleAdPlacement,
      affiliateOffers, saveAffiliateOffer, deleteAffiliateOffer, toggleAffiliateOffer,
      adminBlogPosts, saveBlogPost, deleteBlogPost, getBlogPost,
      locationOverrides, saveLocationOverride, getLocationOverride,
      linkSettings, updateLinkSettings,
      recentActivity, addActivity,
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextType {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
